import '../../../neb-action-bar';
import '../../../neb-table-add';
import '../../../neb-pagination';
import '../../../neb-popup-header';
import '../../../inputs/neb-textfield';

import { html, css } from 'lit';

import {
  CSS_SPACING,
  CSS_BORDER_GREY_2,
  CSS_COLOR_GREY_1,
  CSS_FONT_WEIGHT_BOLD,
  CSS_FONT_SIZE_BODY,
  CSS_COLOR_HIGHLIGHT,
  OVERLAY_WIDTH_EXTRA_LARGE,
} from '../../../../../../neb-styles/neb-variables';
import {
  FEATURE_FLAGS,
  hasFeatureOrBeta,
} from '../../../../../../neb-utils/feature-util';
import { normalizeForSearch } from '../../../../../../neb-utils/formatters';
import {
  CollectionService,
  SORT_DIR,
} from '../../../../../../neb-utils/services/collection';
import Overlay from '../../neb-overlay';

export const ELEMENTS = {
  table: {
    id: 'table',
  },
  description: {
    id: 'description',
  },
  header: {
    id: 'header',
  },
  noChargesMessage: {
    id: 'no-charges-message',
  },
  noResultsMessage: {
    id: 'no-results',
  },
  searchField: {
    id: 'search',
  },
  chargesLink: {
    id: 'charges-link',
  },
  chargeCodesLink: {
    id: 'charge-codes-link',
  },
  actionBar: {
    id: 'action-bar',
  },
  paginationControl: {
    id: 'pagination-control',
  },
};

class NebOverlayLedgerAddCharge extends Overlay {
  static get properties() {
    return {
      isDirty: {
        type: Boolean,
      },
      __allAdded: {
        type: Boolean,
      },
      __currentPageIndex: {
        type: Number,
      },
      __items: {
        type: Array,
      },
      __pageCount: {
        type: Number,
      },
      __searchText: {
        type: String,
      },
      __selectedCharges: {
        type: Array,
      },
      __hasDHChargeUnitsFF: {
        type: Boolean,
        reflect: true,
      },
    };
  }

  initState() {
    super.initState();

    this.__allAdded = false;
    this.__currentPageIndex = 0;
    this.__items = [];
    this.__pageCount = 0;
    this.__searchText = '';
    this.__initialCharges = [];
    this.__selectedCharges = [];
    this.__hasDHChargeUnitsFF = false;
    this.__collectionService = new CollectionService(
      {
        onChange: ({ pageIndex, pageCount, pageItems, searchText }) => {
          this.__currentPageIndex = pageIndex;
          this.__items = pageItems;
          this.__pageCount = pageCount;
          this.__searchText = searchText;
        },

        onCacheItem: ({ code, procedure, description }) =>
          normalizeForSearch([code, procedure, description].join(' ')),

        onSearch: ({ terms, item }) => terms.every(term => item.includes(term)),

        onSort: (a, b) => {
          const [model] = this.model.items;

          const key = Object.prototype.hasOwnProperty.call(model, 'code')
            ? 'code'
            : 'procedure';

          return a[key].localeCompare(b[key]);
        },
      },
      {
        hideInactive: false,
        sortParams: {
          key: '',
          dir: SORT_DIR.ASC,
        },
      },
    );

    this.isDirty = false;
    this.model = {
      config: { tableConfig: [] },
      items: [],
    };
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      addAll: () => {
        const allItems = [
          ...this.__selectedCharges,
          ...this.__collectionService.getFilteredItems(),
        ];
        this.__selectedCharges = [...new Set([...allItems])];
      },
      apply: () => {
        this.isDirty = false;
        this.dismiss(this.__selectedCharges);
      },
      changePage: pageIndex => this.__collectionService.setPageIndex(pageIndex),
      clearSearch: () => this.__collectionService.search(''),
      clickCharges: () => {
        window.location.href = '/settings/#/charges';
      },
      clickChargeCodes: () => {
        window.location.href = '/settings/#/billing-codes/charge';
      },
      search: ({ value }) => this.__collectionService.search(value),
      add: (item, _index) => {
        this.__selectedCharges = [...this.__selectedCharges, item];
      },
    };
  }

  update(changedProps) {
    if (changedProps.has('model') && this.model) {
      this.__collectionService.setItems(this.model.items);

      this.__selectedCharges = this.model.selectedCharges || [];
      this.isDirty = this.model.isDirty;
    }

    if (changedProps.has('__items') || changedProps.has('__selectedCharges')) {
      this.__allAdded = this.__items.length
        ? this.__collectionService
            .getFilteredItems()
            .every(i => this.__selectedCharges.some(c => c.id === i.id))
        : false;
    }

    super.update(changedProps);
  }

  static get styles() {
    return [
      super.styles,
      css`
        .content {
          width: ${OVERLAY_WIDTH_EXTRA_LARGE};
        }

        .icon-chevron {
          height: 12px;
          width: 12px;
          transform: rotate(90deg);
        }

        .main-header {
          display: flex;
          flex-direction: column;
          padding: ${CSS_SPACING};
        }

        .header {
          display: flex;
          align-items: center;
          padding: ${CSS_SPACING};
          border-bottom: ${CSS_BORDER_GREY_2};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .description {
          padding: 5px 0 0 ${CSS_SPACING};
        }

        .add-button {
          padding: ${CSS_SPACING};
        }

        .search-field {
          min-height: 70px;
          margin: 15px ${CSS_SPACING} 20px;
        }

        .no-items-message {
          font-size: ${CSS_FONT_SIZE_BODY};
          font-style: italic;
          color: ${CSS_COLOR_GREY_1};
          padding: 10px ${CSS_SPACING};
        }

        .text-link {
          cursor: pointer;
          font-style: none;
          color: ${CSS_COLOR_HIGHLIGHT};
        }

        .pagination {
          display: flex;
          justify-content: flex-end;
          margin: ${CSS_SPACING};
          flex-shrink: 0;
        }

        .container-table {
          overflow-y: auto;
        }
      `,
    ];
  }

  async connectedCallback() {
    this.__hasDHChargeUnitsFF = await hasFeatureOrBeta(
      FEATURE_FLAGS.DH_CHARGE_UNITS,
    );

    super.connectedCallback();
  }

  __renderHeader() {
    return html`
      <neb-popup-header
        id="${ELEMENTS.header.id}"
        class="main-header"
        title="${this.model.config.title}"
        showCancelButton
        .onCancel="${this.handlers.apply}"
      ></neb-popup-header>
    `;
  }

  __renderDescription() {
    const description =
      this.model.config.description ||
      "Select charges to post against the patient's ledger.";

    return html`
      <div id="${ELEMENTS.description.id}" class="description">
        ${description}
      </div>
    `;
  }

  __renderSearchField() {
    return html`
      <neb-textfield
        id="${ELEMENTS.searchField.id}"
        class="search-field"
        leadingIcon="neb:search"
        label="Enter code or description to filter list below."
        .trailingIcon="${this.__searchText ? 'neb:clear' : ''}"
        .value="${this.__searchText}"
        .onChange="${this.handlers.search}"
        .onClickIcon="${this.handlers.clearSearch}"
      ></neb-textfield>
    `;
  }

  __renderNoResultsMessage() {
    return html`
      <p id="${ELEMENTS.noResultsMessage.id}" class="no-items-message">
        No results.
      </p>
    `;
  }

  __renderNoChargesMessage() {
    return html`
      <p id="${ELEMENTS.noChargesMessage.id}" class="no-items-message">
        There are no Charges or Charge Codes. Please configure
        <span
          id="${ELEMENTS.chargesLink.id}"
          class="text-link"
          @click="${this.handlers.clickCharges}"
          >Charges
        </span>
        or
        <span
          id="${ELEMENTS.chargeCodesLink.id}"
          class="text-link"
          @click="${this.handlers.clickChargeCodes}"
          >Charge Codes
        </span>
        for the practice.
      </p>
    `;
  }

  __renderNoItemsMessage() {
    return this.__searchText
      ? this.__renderNoResultsMessage()
      : this.__renderNoChargesMessage();
  }

  __renderPaginationControl() {
    return this.__items.length
      ? html`
          <neb-pagination
            id="${ELEMENTS.paginationControl.id}"
            class="pagination"
            .currentPage="${this.__currentPageIndex}"
            .onPageChanged="${this.handlers.changePage}"
            .pageCount="${this.__pageCount}"
          ></neb-pagination>
        `
      : '';
  }

  __renderTable() {
    return html`
      <div class="container-table">
        <neb-table-add
          id="${ELEMENTS.table.id}"
          class="header-table"
          .config="${this.model.config}"
          .allAdded="${this.__allAdded}"
          .onAddAll="${this.handlers.addAll}"
          .layout="${this.layout}"
          .onAdd="${this.handlers.add}"
          .items="${this.__items}"
          .selectedItems="${this.__selectedCharges}"
        >
          ${this.__renderNoItemsMessage()}
        </neb-table-add>
      </div>

      ${this.__renderPaginationControl()}
    `;
  }

  __renderActionBar() {
    return html`
      <neb-action-bar
        id="${ELEMENTS.actionBar.id}"
        confirmLabel=""
        cancelLabel="Done"
        .onCancel="${this.handlers.apply}"
      ></neb-action-bar>
    `;
  }

  renderContent() {
    return html`
      ${this.__renderHeader()}${this.__renderDescription()}
      ${this.__renderSearchField()}${this.__renderTable()}
      ${this.__renderActionBar()}
    `;
  }
}

customElements.define(
  'neb-overlay-ledger-add-charge',
  NebOverlayLedgerAddCharge,
);
